import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiGrid, FiUsers, FiTruck, FiCalendar, FiDollarSign, 
  FiTrendingUp, FiPlus, FiArrowRight 
} from 'react-icons/fi';
import { AdminLayout } from '../../components/layout';
import { Card, Loading } from '../../components/common';
import { getCompanyStats, getAllCompanies } from '../../firebase/companies';
import { getAllCarStats } from '../../firebase/cars';
import { getAllBookingStats, getAllBookings } from '../../firebase/bookings';
import { getUserStats } from '../../firebase/users';

const StatCard = ({ title, value, icon: Icon, color, subtext, link }) => (
  <Card className="p-6">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
        {subtext && <p className="text-sm text-gray-500 mt-1">{subtext}</p>}
      </div>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
        <Icon size={24} className="text-white" />
      </div>
    </div>
    {link && (
      <Link 
        to={link} 
        className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 mt-4 font-medium"
      >
        View all <FiArrowRight size={14} />
      </Link>
    )}
  </Card>
);

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    companies: { total: 0, active: 0 },
    cars: { total: 0, active: 0 },
    bookings: { total: 0, totalRevenue: 0, upcoming: 0 },
    users: { total: 0, managers: 0 }
  });
  const [recentCompanies, setRecentCompanies] = useState([]);
  const [recentBookings, setRecentBookings] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Load all stats in parallel with timeout
      const timeoutPromise = (promise, ms = 5000) => Promise.race([
        promise,
        new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), ms))
      ]);

      const [companyStats, carStats, bookingStats, userStats, companies, bookings] = await Promise.all([
        timeoutPromise(getCompanyStats()).catch(() => ({ stats: null })),
        timeoutPromise(getAllCarStats()).catch(() => ({ stats: null })),
        timeoutPromise(getAllBookingStats()).catch(() => ({ stats: null })),
        timeoutPromise(getUserStats()).catch(() => ({ stats: null })),
        timeoutPromise(getAllCompanies()).catch(() => ({ companies: [] })),
        timeoutPromise(getAllBookings()).catch(() => ({ bookings: [] }))
      ]);

      setStats({
        companies: companyStats.stats || { total: 0, active: 0 },
        cars: carStats.stats || { total: 0, active: 0 },
        bookings: bookingStats.stats || { total: 0, totalRevenue: 0, upcoming: 0 },
        users: userStats.stats || { total: 0, managers: 0 }
      });

      // Get recent companies
      setRecentCompanies(companies.companies?.slice(0, 5) || []);
      
      // Get recent bookings
      setRecentBookings(bookings.bookings?.slice(0, 5) || []);

    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Dashboard">
        <div className="flex items-center justify-center h-64">
          <Loading />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Dashboard">
      {/* Welcome Section */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Welcome back, Admin!</h2>
        <p className="text-gray-600 mt-1">Here's what's happening with your platform today.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Companies"
          value={stats.companies.total}
          icon={FiGrid}
          color="bg-blue-500"
          subtext={`${stats.companies.active} active`}
          link="/admin/companies"
        />
        <StatCard
          title="Total Cars"
          value={stats.cars.total}
          icon={FiTruck}
          color="bg-green-500"
          subtext={`${stats.cars.active} available`}
          link="/admin/cars"
        />
        <StatCard
          title="Total Bookings"
          value={stats.bookings.total}
          icon={FiCalendar}
          color="bg-purple-500"
          subtext={`${stats.bookings.upcoming} upcoming`}
          link="/admin/bookings"
        />
        <StatCard
          title="Total Revenue"
          value={`₹${stats.bookings.totalRevenue?.toLocaleString() || 0}`}
          icon={FiDollarSign}
          color="bg-amber-500"
          subtext="All time"
        />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <Link to="/admin/companies/new">
          <Card className="p-6 hover:shadow-md transition-shadow cursor-pointer border-2 border-dashed border-gray-200 hover:border-primary-300">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                <FiPlus size={24} className="text-primary-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Add New Company</h3>
                <p className="text-sm text-gray-500">Onboard a new business</p>
              </div>
            </div>
          </Card>
        </Link>
        
        <Link to="/admin/managers/new">
          <Card className="p-6 hover:shadow-md transition-shadow cursor-pointer border-2 border-dashed border-gray-200 hover:border-primary-300">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <FiUsers size={24} className="text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Assign Manager</h3>
                <p className="text-sm text-gray-500">Add manager to company</p>
              </div>
            </div>
          </Card>
        </Link>
        
        <Link to="/admin/bookings">
          <Card className="p-6 hover:shadow-md transition-shadow cursor-pointer border-2 border-dashed border-gray-200 hover:border-primary-300">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <FiTrendingUp size={24} className="text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">View Reports</h3>
                <p className="text-sm text-gray-500">Check booking analytics</p>
              </div>
            </div>
          </Card>
        </Link>
      </div>

      {/* Recent Data */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Companies */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Recent Companies</h3>
            <Link to="/admin/companies" className="text-sm text-primary-600 hover:text-primary-700">
              View all
            </Link>
          </div>
          {recentCompanies.length > 0 ? (
            <div className="space-y-3">
              {recentCompanies.map((company) => (
                <Link 
                  key={company.id} 
                  to={`/admin/companies/${company.id}`}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center overflow-hidden">
                    {company.logo ? (
                      <img src={company.logo} alt={company.name} className="w-full h-full object-cover" />
                    ) : (
                      <FiGrid className="text-gray-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{company.name}</p>
                    <p className="text-sm text-gray-500">{company.slug}.carbooking.com</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    company.status === 'active' 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {company.status}
                  </span>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FiGrid size={32} className="mx-auto mb-2 opacity-50" />
              <p>No companies yet</p>
              <Link 
                to="/admin/companies/new" 
                className="text-primary-600 hover:text-primary-700 text-sm"
              >
                Add your first company
              </Link>
            </div>
          )}
        </Card>

        {/* Recent Bookings */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Recent Bookings</h3>
            <Link to="/admin/bookings" className="text-sm text-primary-600 hover:text-primary-700">
              View all
            </Link>
          </div>
          {recentBookings.length > 0 ? (
            <div className="space-y-3">
              {recentBookings.map((booking) => (
                <div 
                  key={booking.id} 
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center overflow-hidden">
                    {booking.carDetails?.image ? (
                      <img src={booking.carDetails.image} alt={booking.carDetails.name} className="w-full h-full object-contain bg-gray-100" />
                    ) : (
                      <FiTruck className="text-gray-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {booking.carDetails?.name || 'Unknown Car'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {booking.customerDetails?.name || booking.customerDetails?.email || 'Unknown Customer'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">₹{booking.totalAmount?.toLocaleString()}</p>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      booking.status === 'confirmed' 
                        ? 'bg-green-100 text-green-700' 
                        : booking.status === 'cancelled'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {booking.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FiCalendar size={32} className="mx-auto mb-2 opacity-50" />
              <p>No bookings yet</p>
            </div>
          )}
        </Card>
      </div>

      {/* User Stats */}
      <div className="mt-6">
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">User Overview</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-900">{stats.users.total}</p>
              <p className="text-sm text-gray-500">Total Users</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-900">{stats.users.admins || 1}</p>
              <p className="text-sm text-gray-500">Admins</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-900">{stats.users.managers}</p>
              <p className="text-sm text-gray-500">Managers</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-900">{stats.users.users}</p>
              <p className="text-sm text-gray-500">Customers</p>
            </div>
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
